import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Menu from '@mui/material/Menu';
import Collapse from '@mui/material/Collapse';
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE } from '../../shared/Constants';

interface Project {
  project_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ChatItem {
  chat_id: string;
  title: string;
  updated_at: string;
}

interface ProjectsViewProps {
  onClose: () => void;
  onNavigateToChat: (chatId: string) => void;
  onNewChatInProject: (projectId: string) => void;
  userChats: any[];
}

// ============ Styled Components ============

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 48px 32px;
  min-height: calc(100vh - 120px);
`;

// === Header ===
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 36px;
`;

const BackButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.5rem; }
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Noka', sans-serif;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  & svg { font-size: 1.6rem; opacity: 0.5; }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  transition: all 0.15s ease;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.1rem; }
`;

const IconBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.2rem; }
`;

// === Project Grid ===
const ProjectGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProjectCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  overflow: hidden;
  box-sizing: border-box;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
    border-color: ${({ theme }) => theme.font_color}15;
  }
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
`;

const CardIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.hover_overlay_light};
  flex-shrink: 0;
  & svg { font-size: 1.2rem; color: ${({ theme }) => theme.font_color}; opacity: 0.6; }
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 2px;
`;

const CardMenu = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0;
  transition: opacity 0.12s ease;
  ${ProjectCard}:hover & { opacity: 1; }
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 1rem; }
`;

const CardChats = styled.div`
  padding: 0 20px 16px 74px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardChatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// === Create inline ===
const CreateCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px dashed ${({ theme }) => theme.sidebar_border};
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 1rem;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
    color: ${({ theme }) => theme.font_color};
    border-color: ${({ theme }) => theme.font_color}30;
  }
  & svg { font-size: 1.1rem; }
`;

const InlineInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 4px 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

// === Empty state ===
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 20px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 1rem;
  & svg { font-size: 3rem; opacity: 0.15; }
`;

// === Project Detail ===
const ProjectHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const ProjectTitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: ${({ theme }) => theme.hover_overlay_light};
  flex-shrink: 0;
  & svg { font-size: 1.6rem; color: ${({ theme }) => theme.font_color}; opacity: 0.6; }
`;

const ProjectTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Noka', sans-serif;
  margin: 0;
  flex: 1;
`;

// === Chat input (like ChatGPT) ===
const NewChatInput = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  margin-bottom: 28px;
  cursor: text;
  transition: border-color 0.15s ease;
  &:focus-within { border-color: ${({ theme }) => theme.font_color}40; }
  & svg { font-size: 1.3rem; color: ${({ theme }) => theme.text_secondary}; }
`;

const NewChatInputField = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 1rem;
  outline: none;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

// === Tabs ===
const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar_border};
`;

const Tab = styled.button<{ $active: boolean }>`
  all: unset;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme, $active }) => $active ? theme.font_color : theme.text_secondary};
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.font_color : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: -1px;
  &:hover { color: ${({ theme }) => theme.font_color}; }
`;

// === Chat list ===
const ChatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.1s ease;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;

const ChatName = styled.span`
  flex: 1;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatDate = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  flex-shrink: 0;
`;

const RemoveBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0;
  ${ChatRow}:hover & { opacity: 1; }
  &:hover { color: ${({ theme }) => theme.font_color}; background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1rem; }
`;

// === Add existing tab ===
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  margin-bottom: 16px;
  & svg { font-size: 1.2rem; color: ${({ theme }) => theme.text_secondary}; }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 1rem;
  outline: none;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const AddChatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.1s ease;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;

// === Menu ===
const MenuItemPopup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.2rem; }
`;

// ============ Component ============

const ProjectsView: React.FC<ProjectsViewProps> = ({ onClose, onNavigateToChat, onNewChatInProject, userChats }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [projectChats, setProjectChats] = useState<ChatItem[]>([]);
  const [activeTab, setActiveTab] = useState<'chats' | 'add'>('chats');
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuProjectId, setMenuProjectId] = useState<string | null>(null);
  const [addSearch, setAddSearch] = useState('');
  const [projectChatsCache, setProjectChatsCache] = useState<Record<string, any[]>>({});
  const createRef = useRef<HTMLInputElement>(null);

  const fetchProjects = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/list`, { headers: { Authorization: `Auth ${token}` } });
      if (res.ok) { const data = await res.json(); setProjects(data.projects || []); }
    } catch (e) { console.error('Error fetching projects:', e); }
  }, [getAccessTokenSilently]);

  const fetchProjectChats = useCallback(async (projectId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/chats`, { headers: { Authorization: `Auth ${token}` } });
      if (res.ok) { const data = await res.json(); setProjectChats(data.chats || []); }
    } catch (e) { console.error('Error fetching project chats:', e); }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Fetch chat counts for each project in the list
  useEffect(() => {
    if (projects.length > 0 && !activeProject) {
      projects.forEach(async (p: any) => {
        try {
          const token = await getAccessTokenSilently();
          const res = await fetch(`${API_BASE}/api/projects/${p.project_id}/chats`, { headers: { Authorization: `Auth ${token}` } });
          if (res.ok) {
            const data = await res.json();
            setProjectChatsCache(prev => ({ ...prev, [p.project_id]: data.chats || [] }));
          }
        } catch (e) { /* ignore */ }
      });
    }
  }, [projects, activeProject, getAccessTokenSilently]);

  // Listen for direct project navigation from sidebar
  useEffect(() => {
    const handler = (e: any) => {
      const projectId = e.detail;
      if (projectId) {
        const project = projects.find((p: any) => p.project_id === projectId);
        if (project) {
          setActiveProject(project);
          fetchProjectChats(project.project_id);
          setActiveTab('chats');
        } else {
          // Projects might not be loaded yet, retry after fetch
          fetchProjects().then(() => {
            // Will be handled by next render
          });
        }
      }
    };
    window.addEventListener('navigateToProject', handler);
    return () => window.removeEventListener('navigateToProject', handler);
  }, [projects, fetchProjects, fetchProjectChats]);

  // Listen for "start creating" signal from sidebar "Novo projeto"
  useEffect(() => {
    const handler = () => {
      if (!activeProject) {
        setIsCreating(true);
        setTimeout(() => createRef.current?.focus(), 100);
      }
    };
    window.addEventListener('startCreateProject', handler);
    return () => window.removeEventListener('startCreateProject', handler);
  }, [activeProject]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/create`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) { setNewName(''); setIsCreating(false); fetchProjects(); toast.success("Projeto criado", { autoClose: 2000 }); window.dispatchEvent(new CustomEvent('projectsChanged')); }
    } catch (e) { console.error('Error creating project:', e); }
  };

  const handleRename = async (projectId: string) => {
    const name = editName.trim();
    if (!name) return;
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/rename`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, name }),
      });
      setEditingId(null); toast.success("Projeto renomeado", { autoClose: 2000 });
      fetchProjects(); window.dispatchEvent(new CustomEvent('projectsChanged'));
      if (activeProject?.project_id === projectId) setActiveProject({ ...activeProject, name });
    } catch (e) { console.error('Error renaming:', e); }
  };

  const handleDelete = async (projectId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/delete`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      setMenuAnchor(null); setMenuProjectId(null);
      if (activeProject?.project_id === projectId) { setActiveProject(null); setProjectChats([]); } toast.success("Projeto excluído", { autoClose: 2000 });
      fetchProjects(); window.dispatchEvent(new CustomEvent("projectsChanged"));
    } catch (e) { console.error('Error deleting:', e); }
  };

  const handleAddChat = async (chatId: string) => {
    if (!activeProject) return;
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: activeProject.project_id }),
      });
      fetchProjectChats(activeProject.project_id); window.dispatchEvent(new CustomEvent("projectsChanged"));
      // Stay on "Adicionar existente" tab so user can keep adding more
    } catch (e) { console.error('Error adding chat:', e); }
  };

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const handleRemoveChat = async (chatId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: null }),
      });
      setConfirmRemove(null);
      if (activeProject) fetchProjectChats(activeProject.project_id);
    } catch (e) { console.error('Error removing chat:', e); }
  };

  const [newChatMessage, setNewChatMessage] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);

  const handleNewChatSubmit = async () => {
    if (!activeProject || !newChatMessage.trim() || creatingChat) return;
    const message = newChatMessage.trim();
    setCreatingChat(true);
    try {
      const token = await getAccessTokenSilently();
      // 1. Create chat
      const createRes = await fetch(`${API_BASE}/api/chat/create`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!createRes.ok) { setCreatingChat(false); return; }
      const createData = await createRes.json();
      const chatId = createData.chat_data.chat_id;

      // 2. Associate with project
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: activeProject.project_id }),
      });

      // 3. Update sidebar and add chat to userChats
      setNewChatMessage('');
      setCreatingChat(false);
      window.dispatchEvent(new CustomEvent('projectsChanged'));
      // Add the new chat to userChats so handleChatItemClick finds it
      window.dispatchEvent(new CustomEvent('addChatToList', { detail: createData.chat_data }));

      // 4. Navigate and prefill message
      onNavigateToChat(chatId);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('prefillChatMessage', { detail: message }));
      }, 300);
    } catch (e) {
      console.error('Error creating chat in project:', e);
      setCreatingChat(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const projectChatIds = new Set(projectChats.map(c => c.chat_id));
  const filteredAddChats = addSearch
    ? userChats.filter((c: any) => !projectChatIds.has(c.chat_id) && (c.title || '').toLowerCase().includes(addSearch.toLowerCase()))
    : userChats.filter((c: any) => !projectChatIds.has(c.chat_id));

  // ============ PROJECT LIST VIEW ============
  if (!activeProject) {
    return (
      <Container>
        <Header>
          <BackButton onClick={onClose}><ArrowBackIcon /></BackButton>
          <PageTitle><FolderIcon /> Projetos</PageTitle>
        </Header>

        <p style={{ margin: '0 0 28px 0', fontSize: '1rem', color: 'inherit', opacity: 0.5, lineHeight: 1.5, maxWidth: '600px' }}>
          Agrupe conversas em projetos para manter o contexto compartilhado. Conversas no mesmo projeto podem acessar o que foi discutido nas outras.
        </p>

        <ProjectGrid>
          {isCreating ? (
            <ProjectCard as="div" style={{ cursor: 'default', borderStyle: 'dashed' }}>
              <CardRow>
                <CardIconBox><FolderIcon /></CardIconBox>
                <InlineInput
                  ref={createRef}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setIsCreating(false); setNewName(''); } }}
                  autoFocus
                  placeholder="Nome do projeto"
                  style={{ borderBottom: "2px solid currentColor", background: "rgba(128,128,128,0.1)", borderRadius: "6px", padding: "4px 8px" }}
                />
                <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                  <IconBtn onClick={handleCreate}><CheckIcon /></IconBtn>
                  <IconBtn onClick={() => { setIsCreating(false); setNewName(''); }}><CloseIcon /></IconBtn>
                </div>
              </CardRow>
            </ProjectCard>
          ) : (
            <CreateCard onClick={() => { setIsCreating(true); setTimeout(() => createRef.current?.focus(), 50); }}>
              <AddIcon /> Novo projeto
            </CreateCard>
          )}

          {projects.map((project) => {
            const chats = projectChatsCache[project.project_id] || [];
            return (
              <ProjectCard key={project.project_id} onClick={() => {
                if (editingId !== project.project_id) {
                  setActiveProject(project);
                  fetchProjectChats(project.project_id);
                  setActiveTab('chats');
                }
              }}>
                <CardRow>
                  <CardIconBox><FolderIcon /></CardIconBox>
                  {editingId === project.project_id ? (
                    <>
                      <InlineInput
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(project.project_id); if (e.key === 'Escape') setEditingId(null); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ borderBottom: "2px solid currentColor", background: "rgba(128,128,128,0.1)", borderRadius: "6px", padding: "4px 8px" }}
                        autoFocus
                        placeholder="Nome do projeto"
                      />
                      <IconBtn onClick={(e) => { e.stopPropagation(); handleRename(project.project_id); }}><CheckIcon /></IconBtn>
                    </>
                  ) : (
                    <>
                      <CardContent>
                        <CardName>{project.name}</CardName>
                        <CardMeta>{chats.length > 0 ? `${chats.length} conversa${chats.length > 1 ? 's' : ''}` : 'Sem conversas'}</CardMeta>
                      </CardContent>
                      <CardMenu onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuProjectId(project.project_id); }}>
                        <MoreHorizIcon />
                      </CardMenu>
                    </>
                  )}
                </CardRow>
              </ProjectCard>
            );
          })}
        </ProjectGrid>


        <Menu
          anchorEl={menuAnchor} open={Boolean(menuAnchor)}
          onClose={() => { setMenuAnchor(null); setMenuProjectId(null); }}
          PaperProps={{ sx: (theme) => ({ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? '#232321' : 'white', color: theme.palette.mode === 'dark' ? 'white' : '#232321', boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)', minWidth: '180px' }) }}
          MenuListProps={{ sx: { padding: '8px' } }} TransitionComponent={Collapse}
        >
          <MenuItemPopup onClick={() => { const p = projects.find(p => p.project_id === menuProjectId); if (p) { setEditingId(p.project_id); setEditName(p.name); } setMenuAnchor(null); setMenuProjectId(null); }}>
            <ModeEditOutlineIcon /> <span>Renomear</span>
          </MenuItemPopup>
          <MenuItemPopup onClick={() => { if (menuProjectId) handleDelete(menuProjectId); }}>
            <DeleteOutlineIcon sx={{ color: '#991b1b' }} /> <span style={{ color: '#991b1b' }}>Apagar</span>
          </MenuItemPopup>
        </Menu>
      </Container>
    );
  }

  // ============ PROJECT DETAIL VIEW ============
  return (
    <Container>
      <Header>
        <BackButton onClick={() => { setActiveProject(null); setProjectChats([]); }}>
          <ArrowBackIcon />
        </BackButton>
        <span style={{ fontSize: '0.85rem', color: 'inherit', opacity: 0.5, cursor: 'pointer' }} onClick={() => { setActiveProject(null); setProjectChats([]); }}>
          Projetos
        </span>
        <span style={{ opacity: 0.3 }}>/</span>
      </Header>

      <ProjectHeader>
        <ProjectTitleIcon><FolderIcon /></ProjectTitleIcon>
        {editingId === activeProject.project_id ? (
          <>
            <InlineInput
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(activeProject.project_id); if (e.key === 'Escape') setEditingId(null); }}
              autoFocus
              placeholder="Nome do projeto"
              style={{ fontSize: '1.8rem', fontWeight: 600, fontFamily: "'Noka', sans-serif", borderBottom: '2px solid currentColor', background: 'rgba(128,128,128,0.1)', borderRadius: '6px', padding: '4px 8px' }}
            />
            <IconBtn onClick={() => handleRename(activeProject.project_id)}><CheckIcon /></IconBtn>
            <IconBtn onClick={() => setEditingId(null)}><CloseIcon /></IconBtn>
          </>
        ) : (
          <>
            <ProjectTitle>{activeProject.name}</ProjectTitle>
            <IconBtn onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuProjectId(activeProject.project_id); }}>
              <MoreHorizIcon />
            </IconBtn>
          </>
        )}
      </ProjectHeader>

      <NewChatInput>
        <AddIcon style={{ fontSize: '1.1rem', opacity: 0.5 }} />
        <NewChatInputField
          placeholder={`Novo chat em ${activeProject.name}...`}
          value={newChatMessage}
          onChange={(e) => setNewChatMessage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleNewChatSubmit(); } }}
          disabled={creatingChat}
        />
        {creatingChat && <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>Criando...</span>}
      </NewChatInput>

      <TabRow>
        <Tab $active={activeTab === 'chats'} onClick={() => setActiveTab('chats')}>
          Conversas{projectChats.length > 0 ? ` (${projectChats.length})` : ''}
        </Tab>
        <Tab $active={activeTab === 'add'} onClick={() => setActiveTab('add')}>
          Adicionar existente
        </Tab>
      </TabRow>

      {activeTab === 'chats' && (
        <>
          {projectChats.length === 0 ? (
            <EmptyState>
              <span>Nenhuma conversa neste projeto</span>
              <ActionButton onClick={() => setActiveTab('add')}>
                <AddIcon /> Adicionar conversa existente
              </ActionButton>
            </EmptyState>
          ) : (
            <ChatList>
              {projectChats.map((chat) => (
                <ChatRow key={chat.chat_id}>
                  <ChatName onClick={() => onNavigateToChat(chat.chat_id)}>
                    {chat.title || 'Conversa sem título'}
                  </ChatName>
                  <ChatDate>{formatDate(chat.updated_at)}</ChatDate>
                  {confirmRemove === chat.chat_id ? (
                    <>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>Remover?</span>
                      <IconBtn onClick={(e) => { e.stopPropagation(); handleRemoveChat(chat.chat_id); }} title="Confirmar">
                        <CheckIcon style={{ fontSize: '0.9rem', color: '#991b1b' }} />
                      </IconBtn>
                      <IconBtn onClick={(e) => { e.stopPropagation(); setConfirmRemove(null); }} title="Cancelar">
                        <CloseIcon style={{ fontSize: '0.9rem' }} />
                      </IconBtn>
                    </>
                  ) : (
                    <RemoveBtn onClick={(e) => { e.stopPropagation(); setConfirmRemove(chat.chat_id); }} title="Remover do projeto">
                      <CloseIcon />
                    </RemoveBtn>
                  )}
                </ChatRow>
              ))}
            </ChatList>
          )}
        </>
      )}

      {activeTab === 'add' && (
        <>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              placeholder="Buscar conversas..."
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              autoFocus
            />
          </SearchBox>
          <ChatList>
            {filteredAddChats.map((chat: any) => (
              <AddChatRow key={chat.chat_id} onClick={() => handleAddChat(chat.chat_id)}>
                <ChatName>{chat.title || 'Conversa sem título'}</ChatName>
                <ChatDate>{formatDate(chat.updated_at)}</ChatDate>
                <ActionButton style={{ padding: '6px 14px', fontSize: '0.85rem' }}>
                  <AddIcon style={{ fontSize: '1rem' }} /> Adicionar
                </ActionButton>
              </AddChatRow>
            ))}
            {filteredAddChats.length === 0 && (
              <EmptyState style={{ padding: '30px' }}>
                <span>{addSearch ? 'Nenhuma conversa encontrada' : 'Todas as conversas já estão no projeto'}</span>
              </EmptyState>
            )}
          </ChatList>
        </>
      )}

      <Menu
        anchorEl={menuAnchor} open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuProjectId(null); }}
        PaperProps={{ sx: (theme) => ({ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? '#232321' : 'white', color: theme.palette.mode === 'dark' ? 'white' : '#232321', boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)', minWidth: '180px' }) }}
        MenuListProps={{ sx: { padding: '8px' } }} TransitionComponent={Collapse}
      >
        <MenuItemPopup onClick={() => {
          if (activeProject) { setEditingId(activeProject.project_id); setEditName(activeProject.name); }
          setMenuAnchor(null); setMenuProjectId(null);
        }}>
          <ModeEditOutlineIcon /> <span>Renomear</span>
        </MenuItemPopup>
        <MenuItemPopup onClick={() => { if (menuProjectId) handleDelete(menuProjectId); }}>
          <DeleteOutlineIcon sx={{ color: '#991b1b' }} /> <span style={{ color: '#991b1b' }}>Apagar projeto</span>
        </MenuItemPopup>
      </Menu>
    </Container>
  );
};

export default ProjectsView;
